import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { TransactionPreset } from '@debtbot/shared';
import {
  useConnection,
  useCreateTransaction,
  useDeleteTransaction,
  useMe,
  useTransactions,
} from '../api/hooks';
import { Amount } from '../components/Amount';
import { Button } from '../components/Button';
import { ErrorBanner } from '../components/ErrorBanner';
import { MainButtonFallback } from '../components/MainButtonFallback';
import { PresetChips } from '../components/PresetChips';
import { ListSkeleton } from '../components/Skeleton';
import { useToast } from '../components/Toast';
import { TransactionRow } from '../components/TransactionRow';
import { useT } from '../i18n';
import { fullName, primaryBalance } from '../lib/format';
import { haptic } from '../telegram/haptic';
import { useMainButton } from '../telegram/ui';
import { SettleSheet } from './SettleSheet';

export function PersonScreen() {
  const t = useT();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const me = useMe();
  const conn = useConnection(id);
  const feed = useTransactions(id);
  const create = useCreateTransaction(id, me.data?.id ?? '');
  const remove = useDeleteTransaction(id);
  const toast = useToast();
  const [settleOpen, setSettleOpen] = useState(false);

  const canWrite = !!conn.data && !conn.data.peer.is_deleted;
  const goNew = () => void navigate(`/c/${id}/new`);
  useMainButton({ text: t.person.add, visible: canWrite, onClick: goNew });

  const quickAdd = async (p: TransactionPreset) => {
    haptic.tap();
    try {
      const created = await create.mutateAsync({
        client_request_id: crypto.randomUUID(),
        type: p.type,
        payer_id: p.payer_id,
        amount: p.amount,
        currency: p.currency,
        category: p.category,
        note: p.note,
        expected_balance: null,
      });
      haptic.success();
      toast({
        text: t.person.added,
        action: { label: t.person.undo, onClick: () => remove.mutate(created.id) },
      });
    } catch {
      haptic.error();
    }
  };

  if (conn.isPending || me.isPending) {
    return (
      <main className="min-h-dvh bg-bg text-text px-4 py-4">
        <ListSkeleton rows={3} />
      </main>
    );
  }
  if (conn.isError || !conn.data || !me.data) {
    return (
      <main className="min-h-dvh bg-bg text-text px-4 py-4">
        <ErrorBanner error={conn.error} onRetry={() => void conn.refetch()} />
      </main>
    );
  }

  const c = conn.data;
  const name = c.peer.is_deleted ? t.home.deletedUser : fullName(c.peer);
  const balance = primaryBalance(c.balances);
  const headline =
    balance.amount > 0
      ? t.person.owesYou(name)
      : balance.amount < 0
        ? t.person.youOwe(name)
        : t.person.settled;
  const items = feed.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <main className="min-h-dvh bg-bg text-text px-4 py-4 pb-24 flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">{name}</h1>
        <div className="text-hint">{headline}</div>
        {balance.amount !== 0 && (
          <Amount
            money={{ ...balance, amount: Math.abs(balance.amount) }}
            withSign={false}
            className="text-3xl font-semibold"
          />
        )}
        {c.disputed.length > 0 && (
          <div className="text-sm text-warning">
            ⚠ {t.home.disputedAmount}:{' '}
            <Amount
              money={{ ...c.disputed[0]!, amount: Math.abs(c.disputed[0]!.amount) }}
              withSign={false}
              className="text-warning"
            />
          </div>
        )}
        {c.peer.is_deleted && <div className="text-sm text-hint">{t.person.peerDeleted}</div>}
      </header>

      {canWrite && c.recent_presets.length > 0 && (
        <section className="flex flex-col gap-2">
          <div className="text-sm text-hint">{t.person.quickAdd}</div>
          <PresetChips
            presets={c.recent_presets}
            disabled={create.isPending}
            onPick={(p) => void quickAdd(p)}
          />
        </section>
      )}

      {feed.isPending && <ListSkeleton />}
      {feed.isError && <ErrorBanner error={feed.error} onRetry={() => void feed.refetch()} />}
      {feed.data && items.length === 0 && <p className="text-hint">{t.person.emptyFeed}</p>}
      {items.length > 0 && (
        <section className="flex flex-col gap-2">
          <div className="divide-y divide-secondary-bg">
            {items.map((tx) => (
              <TransactionRow
                key={tx.id}
                tx={tx}
                meId={me.data!.id}
                onClick={() => void navigate(`/tx/${tx.id}`)}
              />
            ))}
          </div>
          {feed.hasNextPage && (
            <Button
              variant="secondary"
              onClick={() => void feed.fetchNextPage()}
              disabled={feed.isFetchingNextPage}
            >
              {t.person.loadMore}
            </Button>
          )}
        </section>
      )}

      {canWrite && balance.amount !== 0 && (
        <Button variant="secondary" data-testid="settle-open" onClick={() => setSettleOpen(true)}>
          {t.person.settle}
        </Button>
      )}

      <SettleSheet
        open={settleOpen}
        onClose={() => setSettleOpen(false)}
        connection={c}
        me={me.data}
        onDone={() => setSettleOpen(false)}
      />
      <MainButtonFallback text={t.person.add} visible={canWrite} onClick={goNew} />
    </main>
  );
}
